import Cookies from 'universal-cookie';
import { isMockMode } from '../apiMode';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8008/api';

// Known completed video analysis used for chat tests
const CHAT_TEST_ANALYSIS_UUID = '673a518e-8ae6-4c37-a529-2524478f8abb';
const CHAT_TEST_TEAM_UUID     = 'e95d3714-ebd2-44d9-b1a6-a4d7047eb6ac';
const CHAT_POLL_INTERVAL_MS   = 2000;
const CHAT_POLL_TIMEOUT_MS    = 60000;
const LLM_ERROR_MARKER        = 'Service temporarily unavailable';

function assert(condition, message) {
  if (!condition) throw new Error(message);
}

function getToken() {
  const cookies = new Cookies();
  return cookies.get('token');
}

async function authFetch(path, options = {}) {
  const token = getToken();
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
  });
  if (res.status === 401 || res.status === 403) {
    throw new Error('Not authenticated — log in first, then re-run the tests');
  }
  return res;
}

async function pollChatAnswer(fragmentUuid, timeoutMs = CHAT_POLL_TIMEOUT_MS) {
  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    const res = await authFetch(`/analysis/get_answer/${fragmentUuid}`);
    if (!res.ok) throw new Error(`Poll returned ${res.status}`);
    const data = await res.json();
    if (data.status === 'Processed') return data;
    if (data.status === 'Failed to process') throw new Error('Job failed on the server');
    await new Promise(r => setTimeout(r, CHAT_POLL_INTERVAL_MS));
  }
  throw new Error(`Chat answer not ready after ${timeoutMs / 1000}s — worker may be down`);
}

function makeChatTest(id, name, prompt, extraAssert, mockAnswer) {
  return {
    id,
    name,
    run: async () => {
      if (isMockMode()) {
        assert(mockAnswer && mockAnswer.length > 0, 'Mock answer is empty');
        if (extraAssert) extraAssert(mockAnswer);
        return {
          snapshot: `<pre style="white-space:pre-wrap;font-family:monospace;font-size:12px">[MOCK] Elapsed: 0ms\nFragment: mock-uuid\n\nQ: ${prompt}\n\nA: ${mockAnswer}</pre>`,
          logs: [],
        };
      }

      const start = Date.now();

      const res = await authFetch('/analysis/chat', {
        method: 'POST',
        body: JSON.stringify({
          analysis_uuid: CHAT_TEST_ANALYSIS_UUID,
          team_uuid:     CHAT_TEST_TEAM_UUID,
          prompt,
        }),
      });
      if (!res.ok) {
        const body = await res.text();
        throw new Error(`Chat POST returned ${res.status}: ${body.slice(0, 200)}`);
      }
      const fragment = await res.json();
      assert(fragment.uuid, 'No fragment UUID returned from chat endpoint');

      const answered = await pollChatAnswer(fragment.uuid);
      const elapsedMs = Date.now() - start;

      assert(answered.result && answered.result.trim().length > 0, 'Answer is empty');
      assert(
        !answered.result.includes(LLM_ERROR_MARKER),
        `LLM returned error fallback: "${answered.result.slice(0, 120)}"`
      );
      if (extraAssert) extraAssert(answered.result);

      return {
        snapshot: `<pre style="white-space:pre-wrap;font-family:monospace;font-size:12px">Elapsed: ${elapsedMs}ms\nFragment: ${fragment.uuid}\n\nQ: ${prompt}\n\nA: ${answered.result}</pre>`,
        logs: [],
      };
    },
  };
}

async function callLlmTest(test) {
  const token = getToken();
  const res = await fetch(`${API_BASE}/dev/llm-test`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ test }),
  });

  if (res.status === 401 || res.status === 403) {
    throw new Error('Not authenticated — log in first, then re-run the tests');
  }
  if (!res.ok) {
    const body = await res.text();
    throw new Error(`Backend returned ${res.status}: ${body.slice(0, 200)}`);
  }
  return res.json();
}

function makeTest(id, name, testKey, validate, mockData) {
  return {
    id,
    name,
    run: async () => {
      if (isMockMode()) {
        const data = typeof mockData === 'function' ? mockData() : mockData;
        if (validate) validate(data);
        return {
          snapshot: `<pre style="white-space:pre-wrap;font-family:monospace;font-size:12px">[MOCK]\n${JSON.stringify(data, null, 2)}</pre>`,
          logs: [],
        };
      }
      const data = await callLlmTest(testKey);
      if (validate) validate(data);
      return {
        snapshot: `<pre style="white-space:pre-wrap;font-family:monospace;font-size:12px">${JSON.stringify(data, null, 2)}</pre>`,
        logs: [],
      };
    },
  };
}

export const llmPipelineTests = [

  makeTest(
    'llm-fixtures-present',
    'Test fixtures: all 3 files exist in private storage',
    'fixtures_present',
    (data) => assert(data.passed, data.details),
    {
      passed: true,
      details: 'Mock: all test fixtures present',
      sizes: { 'test-pt.mp3': '20.00MB', 'test-en.mp4': '17.00MB', 'test-en.pdf': '1.80MB' },
    }
  ),

  makeTest(
    'llm-connectivity',
    'LLM Gateway: connectivity — gateway responds to a simple prompt',
    'connectivity',
    (data) => {
      assert(data.passed, data.details);
      assert(data.latency_ms < 30000, `Response too slow: ${data.latency_ms}ms`);
      assert(data.response && data.response.trim().length > 0, 'Empty response from gateway');
      assert(!data.response.includes('Service temporarily unavailable'), `Gateway error: ${data.response}`);
    },
    { passed: true, details: 'Mock: gateway responded in 42ms', response: 'OK', latency_ms: 42 }
  ),

  makeTest(
    'llm-plain-text-en',
    'LLM (EN): English summary — non-empty response using test-en.mp4 transcript',
    'plain_text_en',
    (data) => {
      assert(data.passed, data.details);
      assert(data.response && data.response.trim().length > 20, 'Response too short');
      assert(data.request_id && data.request_id.startsWith('assemblyai_'), `Bad request_id: ${data.request_id}`);
    },
    {
      passed: true,
      details: 'Mock: summary generated',
      response: 'The meeting discussed quarterly revenue results and future planning. Revenue increased by fifteen percent driven by new client acquisitions and enterprise tier expansion.',
      request_id: 'assemblyai_mock_en_summary',
      latency_ms: 1200,
    }
  ),

  makeTest(
    'llm-plain-text-pt',
    'LLM (PT): Portuguese summary — non-empty response using test-pt.mp3 transcript',
    'plain_text_pt',
    (data) => {
      assert(data.passed, data.details);
      assert(data.response && data.response.trim().length > 20, 'Response too short');
    },
    {
      passed: true,
      details: 'Mock: resumo gerado',
      response: 'A reunião discutiu os resultados trimestrais e o planeamento futuro. A receita aumentou quinze por cento impulsionada por novas aquisições de clientes e expansão do nível empresarial.',
      latency_ms: 1400,
    }
  ),

  makeTest(
    'llm-no-markdown-en',
    'LLM (EN): no markdown — bullet summary contains no markdown syntax',
    'no_markdown_en',
    (data) => {
      assert(data.passed, data.details);
      assert(data.response, 'No response returned');
    },
    {
      passed: true,
      details: 'Mock: response contains no markdown syntax',
      response: 'Revenue grew fifteen percent. New client acquisitions drove growth. Customer success needs more resources. Team morale remains high despite demanding schedule.',
    }
  ),

  makeTest(
    'llm-no-markdown-pt',
    'LLM (PT): no markdown — Portuguese bullet summary contains no markdown syntax',
    'no_markdown_pt',
    (data) => {
      assert(data.passed, data.details);
      assert(data.response, 'No response returned');
    },
    {
      passed: true,
      details: 'Mock: resposta sem markdown',
      response: 'A receita cresceu quinze por cento. Novas aquisicoes de clientes impulsionaram o crescimento. O sucesso do cliente precisa de mais recursos. O moral permanece elevado.',
    }
  ),

  makeTest(
    'llm-generate-task-en',
    'LLM (EN): generateTask() — returns {response, request_id} shape for English tasks',
    'generate_task_en',
    (data) => {
      assert(data.passed, data.details);
      assert(data.request_id && data.request_id.startsWith('assemblyai_'), `Bad request_id: ${data.request_id}`);
      assert(data.response && data.response.trim().length > 0, 'Empty response');
    },
    {
      passed: true,
      details: 'Mock: generateTask returned correct shape',
      response: 'Prepare a budget proposal for the next meeting. Allocate more resources to the customer success team.',
      request_id: 'assemblyai_mock_task_en',
    }
  ),

  makeTest(
    'llm-generate-task-pt',
    'LLM (PT): generateTask() — returns {response, request_id} shape for Portuguese tasks',
    'generate_task_pt',
    (data) => {
      assert(data.passed, data.details);
      assert(data.request_id && data.request_id.startsWith('assemblyai_'), `Bad request_id: ${data.request_id}`);
      assert(data.response && data.response.trim().length > 0, 'Empty response');
    },
    {
      passed: true,
      details: 'Mock: generateTask devolveu estrutura correta',
      response: 'Preparar proposta de orçamento para a próxima reunião. Alocar mais recursos para a equipa de sucesso do cliente.',
      request_id: 'assemblyai_mock_task_pt',
    }
  ),

  makeTest(
    'llm-sentiment-en',
    'LLM (EN): sentiment format — response matches Word-XX% pattern',
    'sentiment_en',
    (data) => {
      assert(data.passed, data.details);
      assert(/\w+-\d+%/.test(data.response || ''), `Response does not match sentiment format: ${data.response}`);
    },
    {
      passed: true,
      details: 'Mock: sentiment response matches expected format',
      response: 'Engagement-35%, Satisfaction-30%, Confidence-20%, Enthusiasm-15%',
    }
  ),

  makeTest(
    'llm-model-resolution',
    'LLM: legacy model name resolution — anthropic/claude-3-* maps to gateway model IDs',
    'model_resolution',
    (data) => {
      assert(data.passed, data.details);
      if (data.results) {
        Object.entries(data.results).forEach(([model, status]) => {
          assert(status === 'OK', `Model "${model}" failed: ${status}`);
        });
      }
    },
    {
      passed: true,
      details: 'Mock: all legacy model names resolved and responded',
      results: {
        'anthropic/claude-3-haiku':  'OK',
        'anthropic/claude-3-sonnet': 'OK',
        'default':                   'OK',
        'best':                      'OK',
      },
    }
  ),

  // ─── Chat flow tests (queue-based, ~15–30s each in real mode) ─────────────

  makeChatTest(
    'llm-chat-video-factual',
    'Chat (video): factual question answered from transcript via queue',
    'Who are the two speakers in this recording and what are they doing?',
    (answer) => {
      assert(answer.length > 30, 'Answer too short to be meaningful');
    },
    'The recording features two speakers, Speaker A and Speaker B, who are conducting a business meeting to review quarterly revenue results and plan the next steps for the upcoming period.'
  ),

  makeChatTest(
    'llm-chat-video-sentiment',
    'Chat (video): sentiment question returns contextual plain-text answer',
    'What was the overall mood or energy level of the speakers?',
    (answer) => {
      assert(answer.length > 20, 'Answer too short');
      assert(!/^#{1,6}\s|\*\*|\[.*\]\(/.test(answer), 'Answer contains markdown');
    },
    'The overall mood was positive and energetic. Both speakers showed high engagement and confidence throughout the discussion, with an upbeat attitude toward the revenue results and future plans.'
  ),

  makeChatTest(
    'llm-chat-video-no-hallucination',
    'Chat (video): answer is grounded in transcript, not a generic error',
    'What specific play or action was mentioned first in the recording?',
    (answer) => {
      assert(!answer.toLowerCase().includes('i don\'t have'), 'LLM claims no context — transcript may not be passed');
      assert(!answer.includes('[]'), 'Empty brackets detected — transcript not passed to LLM');
    },
    'The first action mentioned in the recording was reviewing the quarterly financial results, specifically the fifteen percent revenue increase compared to the previous quarter.'
  ),
];
